import { BridgeProxy, Platform } from 'pos/common';

import { ReaderStatus } from 'pos/common/BridgeProxy.Endpoints';
import * as UserActivityMonitor from '../UserActivityMonitor';
import React from 'react';
import { Res } from 'core/api';
import { useIsOnTop } from 'core/ui-next/modal/ModalHooks';

export async function start(onStatusRead: (status: ReaderStatus) => void): Promise<() => void> {

	// Otra opción es hacernos un Nullfingerprint
	if (!Platform.canUseFingerprint())
		return () => { };

	onStatusRead({ failure: '', message: Res.Msg.MSG_INIT_FINGER_PRINT_READER(), verifiedUserId: null });

	// Como el lector ahora se mantiene abierto continuamente, descarto el estado que se hubiera guardado mientras estaba desactivado para evitar
	// mostrar algún error o loguear algún usuario. Otra opción hubiera sido meter dos llamadas más una de Pause y otra de Resume. Pero quiero evitar
	// llamadas de más para que sea más rápido si es posible.
	await BridgeProxy.invoke('NO_REGISTER_USER_ACTIVITY', BridgeProxy.Endpoints.Fingerprint.Status);

	onStatusRead({ failure: '', message: Res.Msg.MSG_FINGERPRINT_READER_READY(), verifiedUserId: null });

	let stopping = false;
	let refreshing = false;

	const refreshStatusInterval = setInterval(async () => {
		// Evito reentradas
		if (stopping || refreshing)
			return;

		try {
			refreshing = true;

			const result = await BridgeProxy.invoke(
				'NO_REGISTER_USER_ACTIVITY',
				BridgeProxy.Endpoints.Fingerprint.Status
			);

			// Si me han detenido mientras invocaba al proxy, salgo
			if (stopping)
				return;

			if (result.status.verifiedUserId) {
				UserActivityMonitor.registerUserActivity();
			}

			onStatusRead(result.status);
		}
		finally {
			refreshing = false;
		}
	}, 200);

	return function () {
		stopping = true;
		clearInterval(refreshStatusInterval);
		onStatusRead({ failure: '', message: Res.Msg.MSG_FINGERPRINT_READER_STOPPED(), verifiedUserId: null });
	};
}

export type FingerprintReader = {
	start: () => Promise<void>;
	stop: () => void;
};

function createFingerprintReader(onStatusRead: (status: ReaderStatus) => void): FingerprintReader {

	let dispose: Nullable<() => void> = null;
	let currentReaderId: object | null = null;

	return {
		start: async () => {
			const newReaderId = {};
			currentReaderId = newReaderId;

			// Si hay alguien arrancado lo quitamos, no queremos más intervals
			dispose?.();
			dispose = null;

			const localDispose = await start(onStatusRead);

			// si hicieron stop() mientras esperaba
			if (currentReaderId !== newReaderId) {
				localDispose();
				return;
			}

			dispose = localDispose;
		},
		stop: () => {
			currentReaderId = null;
			dispose?.();
			dispose = null;
		}
	};
}

export function useFingerprintReaderRef(onFingerprintRead: (status: ReaderStatus) => void): React.RefObject<Nullable<FingerprintReader>> {

	const fingerprintReader = React.useRef<FingerprintReader>(null);
	const onFingerprintReadRef = React.useRef(onFingerprintRead);

	const isOnTop = useIsOnTop();

	React.useLayoutEffect(() => {
		onFingerprintReadRef.current = onFingerprintRead;
	}, [onFingerprintRead]);


	const onStatusRead = React.useCallback((status: ReaderStatus) => {
		onFingerprintReadRef.current(status);
	}, []);

	React.useEffect(() => {

		const reader = createFingerprintReader(onStatusRead);
		fingerprintReader.current = reader;

		return () => {
			reader.stop();
		};
	}, [onStatusRead]);

	// Arranco y paro el reader en base a si estoy on top o no
	React.useEffect(() => {
		if (isOnTop)
			void fingerprintReader.current?.start();
		else
			fingerprintReader.current?.stop();
	}, [isOnTop]);


	return fingerprintReader;
}

